import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FC,
} from "react";
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  SelectOutlined,
  UpOutlined,
} from "@ant-design/icons";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button, FileDropzone, Modal } from "../../../shared";

import { ASSET_FIELDS, type AssetKey } from "./assetsSectionData";

import styles from "./AssetsSection.module.css";

export type AssetFiles = Partial<Record<AssetKey, File | null>>;

export type AssetsSectionProps = {
  /**
   * Controlled upload map. When omitted the section owns its own state
   * (standalone use / tests); when the page lifts the state up it passes the
   * value + change handler so uploads drive the live preview.
   */
  files?: AssetFiles;
  onFileChange?: (key: AssetKey, file: File | null) => void;
  /**
   * Default image URL per slot (resolved from TCM, see
   * `gamePassDefaultAssets`). A slot with a default renders that image instead
   * of an empty dropzone until the admin uploads a replacement.
   */
  defaultUrls?: Partial<Record<AssetKey, string>>;
  /**
   * Slots whose displayed image is a *saved theme override* (not the TCM
   * default). These get a delete affordance even without an in-session upload,
   * so the admin can reset the slot back to the TCM default (the page turns
   * that into a `null` in the theme PATCH).
   */
  resettableSlots?: ReadonlySet<AssetKey>;
  /**
   * Locks every tile (no upload / replace / delete) while a save is in flight,
   * so an edit can't race the merged response that re-renders the tiles.
   */
  disabled?: boolean;
};

type AssetUploadTileProps = {
  label: string;
  value: File | null;
  /** Default (TCM) image shown when nothing has been uploaded yet. */
  defaultUrl?: string;
  onChange: (file: File | null) => void;
  /** Opens the delete-confirmation popup for this tile's image. */
  onRequestDelete: () => void;
  /**
   * When true, show the delete button even without an in-session upload — the
   * displayed image is a saved theme override that can be reset to default.
   */
  resettable?: boolean;
  /** Locks the tile (no upload / replace / delete) while a save is in flight. */
  disabled?: boolean;
  /** Root `data-testid`; the input/preview sub-nodes derive from it. */
  testId: string;
};

/**
 * One asset slot: a labelled drag-and-drop upload tile.
 *
 * Empty, it reuses the shared {@link FileDropzone} (the same primitive the
 * challenge / task editors use). Once a file is picked it swaps to a custom
 * preview that matches the Figma — the image contained on the dark tile
 * surface with a pencil "replace" button in the corner — instead of the
 * shared primitive's filename header + white preview well.
 *
 * Slots with a TCM default (`defaultUrl`) render that image up front with a
 * replace button only — the default itself can't be deleted, so the delete
 * action appears only once the admin has uploaded their own file.
 */
const AssetUploadTile: FC<AssetUploadTileProps> = ({
  label,
  value,
  defaultUrl,
  onChange,
  onRequestDelete,
  resettable = false,
  disabled = false,
  testId,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Own the object-URL lifecycle inside the effect so the create/revoke pair
  // always lines up (incl. React 18 StrictMode's mount → unmount → remount).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return undefined;
    }
    const blobUrl = URL.createObjectURL(value);
    setPreviewUrl(blobUrl);
    return () => URL.revokeObjectURL(blobUrl);
  }, [value]);

  const openPicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleReplace = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.files?.[0] ?? null;
      if (next) onChange(next);
      // Reset so re-picking the same file still fires `change`.
      event.target.value = "";
    },
    [onChange],
  );

  const isUpload = Boolean(value && previewUrl);
  const displaySrc = isUpload ? previewUrl : defaultUrl;
  // The default (TCM) image can't be deleted; the delete action appears once
  // the admin has uploaded their own file OR when the tile shows a saved theme
  // override that can be reset back to the default.
  const canDelete = isUpload || resettable;

  return (
    <div className={styles.tile}>
      <span className={styles.tileLabel}>{label}</span>
      {displaySrc ? (
        <div className={styles.preview} data-testid={`${testId}-preview`}>
          <img
            src={displaySrc}
            alt={isUpload && value ? value.name : label}
            className={styles.previewImage}
            data-testid={`${testId}-preview-image`}
          />
          <div className={styles.previewActions}>
            <button
              type="button"
              className={styles.previewAction}
              onClick={openPicker}
              disabled={disabled}
              aria-label={AppStrings.GAME_PASS_STYLE_ASSETS_REPLACE_ARIA}
              data-testid={`${testId}-replace`}
            >
              <EditOutlined aria-hidden="true" />
            </button>
            {canDelete && (
              <button
                type="button"
                className={`${styles.previewAction} ${styles.previewActionDanger}`}
                onClick={onRequestDelete}
                disabled={disabled}
                aria-label={AppStrings.GAME_PASS_STYLE_ASSETS_DELETE_ARIA}
                data-testid={`${testId}-delete`}
              >
                <DeleteOutlined aria-hidden="true" />
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className={styles.hiddenInput}
            aria-label={AppStrings.GAME_PASS_STYLE_ASSETS_REPLACE_ARIA}
            onChange={handleReplace}
            data-testid={`${testId}-input`}
          />
        </div>
      ) : (
        <FileDropzone
          value={null}
          onChange={onChange}
          disabled={disabled}
          accept="image/*"
          icon={<SelectOutlined />}
          heading={AppStrings.GAME_PASS_STYLE_ASSETS_DRAG_AND_DROP}
          hint={AppStrings.GAME_PASS_STYLE_ASSETS_ASPECT_RATIO}
          buttonLabel={AppStrings.GAME_PASS_STYLE_ASSETS_UPLOAD}
          className={styles.dropzone}
          data-testid={testId}
        />
      )}
    </div>
  );
};

/**
 * Game Pass → Style → Assets.
 *
 * Renders a collapsible "Assets" card with one drag-and-drop upload tile per
 * themeable Game Pass asset (Cash, Coins, Challenge Complete Icon, …). The
 * picked `File` is held in the (lifted) state so each tile shows its inline
 * preview; the page presigns + uploads it and PATCHes the resulting URL on
 * Save. Deleting a saved override (a `resettableSlots` slot) clears it back to
 * the TCM default, which the page persists as a `null` in the theme PATCH.
 */
const AssetsSection: FC<AssetsSectionProps> = ({
  files: filesProp,
  onFileChange,
  defaultUrls,
  resettableSlots,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [internalFiles, setInternalFiles] = useState<AssetFiles>({});
  const files = filesProp ?? internalFiles;
  // The asset whose image the admin is confirming deletion of, or `null` when
  // the confirm popup is closed. Lifted here (one modal for the whole grid)
  // rather than per-tile so only a single dialog ever mounts.
  const [pendingDeleteKey, setPendingDeleteKey] = useState<AssetKey | null>(
    null,
  );

  const handleToggle = useCallback(() => setIsExpanded((open) => !open), []);

  const handleChange = useCallback(
    (key: AssetKey) => (file: File | null) => {
      if (onFileChange) {
        onFileChange(key, file);
        return;
      }
      setInternalFiles((prev) => ({ ...prev, [key]: file }));
    },
    [onFileChange],
  );

  const handleRequestDelete = useCallback(
    (key: AssetKey) => () => setPendingDeleteKey(key),
    [],
  );

  const handleCancelDelete = useCallback(() => setPendingDeleteKey(null), []);

  // Confirm clears the asset locally so the tile (and the live preview) drop
  // back to the default. The page turns a cleared saved-override into a `null`
  // in the theme PATCH on Save.
  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteKey) {
      if (onFileChange) {
        onFileChange(pendingDeleteKey, null);
      } else {
        setInternalFiles((prev) => ({ ...prev, [pendingDeleteKey]: null }));
      }
    }
    setPendingDeleteKey(null);
  }, [pendingDeleteKey, onFileChange]);

  const pendingDeleteLabel = ASSET_FIELDS.find(
    (field) => field.key === pendingDeleteKey,
  )?.label;

  return (
    <section className={styles.card} data-testid="game-pass-style-assets">
      <button
        type="button"
        className={styles.header}
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-label={AppStrings.GAME_PASS_STYLE_ASSETS_TOGGLE_ARIA}
        data-testid="game-pass-style-assets-toggle"
      >
        <h2 className={styles.title}>
          {AppStrings.GAME_PASS_STYLE_ASSETS_TITLE}
        </h2>
        {isExpanded ? (
          <UpOutlined className={styles.chevron} aria-hidden="true" />
        ) : (
          <DownOutlined className={styles.chevron} aria-hidden="true" />
        )}
      </button>

      {isExpanded && (
        <div className={styles.grid} data-testid="game-pass-style-assets-grid">
          {ASSET_FIELDS.map(({ key, label }) => (
            <AssetUploadTile
              key={key}
              label={label}
              value={files[key] ?? null}
              defaultUrl={defaultUrls?.[key]}
              onChange={handleChange(key)}
              onRequestDelete={handleRequestDelete(key)}
              resettable={resettableSlots?.has(key)}
              disabled={disabled}
              testId={`game-pass-style-asset-${key}`}
            />
          ))}
        </div>
      )}

      <Modal
        open={pendingDeleteKey !== null}
        onClose={handleCancelDelete}
        intent="destructive"
        size="sm"
        destroyOnHidden
        title={
          <span className={styles.deleteModalTitle}>
            {AppStrings.GAME_PASS_STYLE_ASSETS_DELETE_MODAL_TITLE}
          </span>
        }
        footer={
          <div className={styles.deleteModalFooter}>
            <Button
              intent="neutral"
              variant="outline"
              onClick={handleCancelDelete}
              data-testid="game-pass-style-assets-delete-go-back"
            >
              {AppStrings.GAME_PASS_STYLE_ASSETS_DELETE_GO_BACK}
            </Button>
            <Button
              intent="danger"
              variant="outline"
              style={{ color: "var(--COLOR-LOST_COLOR)" }}
              onClick={handleConfirmDelete}
              data-testid="game-pass-style-assets-delete-confirm"
            >
              {AppStrings.GAME_PASS_STYLE_ASSETS_DELETE_CONFIRM_CTA}
            </Button>
          </div>
        }
      >
        <div data-testid="game-pass-style-assets-delete-modal">
          <p className={styles.deleteModalBody}>
            {`${AppStrings.GAME_PASS_STYLE_ASSETS_DELETE_CONFIRM_PREFIX} `}
            <span className={styles.deleteModalBodyName}>
              {`"${pendingDeleteLabel ?? ""}"`}
            </span>
          </p>
        </div>
      </Modal>
    </section>
  );
};

export default AssetsSection;
